import React, { useState } from 'react'
import { Box, Typography, IconButton, Stack, Button, Modal, Fade, Backdrop, TextField, Container, Grid, InputAdornment } from '@mui/material'
import { Email, Facebook, Instagram, WhatsApp, Close, LocationOn, AccessTime, RateReview, Person } from '@mui/icons-material'
import { FaStar } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#f8fafc',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#002855', borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#002855' },
}

const ContactRow = ({ icon, label, value, href, onClick }) => (
  <Box
    component={href ? 'a' : 'div'}
    href={href}
    target={href ? '_blank' : undefined}
    rel={href ? 'noopener noreferrer' : undefined}
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 2,
      textDecoration: 'none', color: 'inherit',
      cursor: href || onClick ? 'pointer' : 'default',
      '&:hover .cv': { color: href || onClick ? '#ff6d00' : 'inherit' },
      transition: 'all 0.2s',
    }}
  >
    <Box sx={{
      width: 38, height: 38, borderRadius: '10px', flexShrink: 0, mt: 0.2,
      bgcolor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, mb: 0.3 }}>
        {label}
      </Typography>
      <Typography className="cv" sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', fontWeight: 600, lineHeight: 1.6, transition: 'color 0.2s' }}>
        {value}
      </Typography>
    </Box>
  </Box>
)

const Footer = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const ratingLabels = t('feedback.ratingLabels', { returnObjects: true })

  const handleNav = (sectionId) => {
    if (location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/')
      setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }
  const [open, setOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [formData, setFormData] = useState({ name: '', message: '' })

  const handleSend = () => {
    const text = `*Feedback Baru*\n*Nama:* ${formData.name}\n*Rating:* ${rating}/5\n*Pesan:* ${formData.message}`
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(text)}`, '_blank')
    setOpen(false)
  }

  const openWA = () =>
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent('Halo CV Mulia Utama Mandiri, saya mengetahui kontak ini melalui website Anda dan ingin bertanya lebih lanjut mengenai layanan yang tersedia. Terima kasih.')}`, '_blank')

  return (
    <Box component="footer" sx={{ bgcolor: '#021428', color: 'white' }}>

      {/* ── FEEDBACK BANNER ── */}
      <Box sx={{
        position: 'relative', py: { xs: 5, md: 6.5 },
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #001533 0%, #002255 60%, #002f6c 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        <Box sx={{ position: 'absolute', right: '-5%', top: '-40%', width: '35%', paddingBottom: '35%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,109,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={{ xs: 4, md: 2 }}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Stack direction="row" spacing={0.6} justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mb: 2 }}>
                {[1, 2, 3, 4, 5].map(i => <FaStar key={i} style={{ color: '#ff6d00', fontSize: '1rem' }} />)}
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', fontWeight: 600, ml: 1, alignSelf: 'center' }}>
                  5.0 dari pelanggan kami
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.55rem', md: '2rem' }, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>
                {t('feedback.header')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', maxWidth: 440, lineHeight: 1.65 }}>
                {t('feedback.subtext')}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <Button
                onClick={() => setOpen(true)}
                startIcon={<RateReview sx={{ fontSize: '1.1rem' }} />}
                sx={{
                  bgcolor: '#ff6d00', color: 'white', px: { xs: 3.5, md: 4 }, py: 1.75,
                  borderRadius: '50px', fontWeight: 800, fontSize: '0.92rem', textTransform: 'none',
                  boxShadow: '0 8px 28px rgba(255,109,0,0.35)',
                  '&:hover': { bgcolor: '#e65100', boxShadow: '0 12px 32px rgba(255,109,0,0.48)', transform: 'translateY(-2px)' },
                  transition: 'all 0.25s ease',
                }}
              >
                {t('feedback.text')}
              </Button>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ── MAIN BODY ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 5, md: 6 }} alignItems="flex-start">

          {/* LEFT — Brand */}
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', letterSpacing: 0.5, color: '#fff', lineHeight: 1.2 }}>
              {t('footer.companyName')}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#ff6d00', fontStyle: 'italic', mt: 0.5, mb: 2.5, fontWeight: 600 }}>
              {t('home.subtitle')}
            </Typography>

            <Typography sx={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.48)', lineHeight: 1.85, mb: 4 }}>
              {t('footer.companyDescription')}
            </Typography>

            {/* Stats */}
            <Stack direction="row" sx={{ mb: 4, borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)', py: 2.5 }}>
              {[
                { value: '5+',   label: t('hero.awards') },
                { value: '200+', label: t('hero.clients') },
                { value: '500+', label: t('hero.placements') },
              ].map((s, i) => (
                <Box key={i} sx={{ flex: 1, textAlign: 'center', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', color: '#ff6d00', lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.63rem', color: 'rgba(255,255,255,0.35)', mt: 0.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Social icons */}
            <Stack direction="row" spacing={1}>
              {[
                { icon: <WhatsApp sx={{ fontSize: 17 }} />, action: openWA },
                { icon: <Email sx={{ fontSize: 17 }} />, href: 'mailto:cvmuliautamamandiri@gmail.com' },
                { icon: <Facebook sx={{ fontSize: 17 }} />, href: 'https://www.facebook.com/p/CV-MULIA-UTAMA-Mandiri-100087263102816/' },
                { icon: <Instagram sx={{ fontSize: 17 }} />, href: 'https://www.instagram.com/cvmuliautamamandiri_official/' },
              ].map(({ icon, href, action }, i) => (
                <IconButton
                  key={i}
                  component={href ? 'a' : 'button'}
                  href={href}
                  target={href ? '_blank' : undefined}
                  rel={href ? 'noopener noreferrer' : undefined}
                  onClick={action}
                  sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.5)',
                    '&:hover': { bgcolor: '#ff6d00', color: '#fff', borderColor: '#ff6d00' },
                    transition: 'all 0.2s',
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* MIDDLE — Links */}
          <Grid item xs={6} md={3}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 2, mb: 3.5 }}>
              {t('footer.linksTitle')}
            </Typography>
            <Stack spacing={1.75}>
              {[
                { label: t('navbar.beranda'),  scroll: 'beranda' },
                { label: t('navbar.layanan'),  scroll: 'layanan' },
                { label: t('navbar.tentang'),  scroll: 'tentang' },
                { label: t('navbar.galeri'),   scroll: 'galeri' },
                { label: t('navbar.kontak'),   scroll: 'kontak' },
                { label: t('navbar.faq'),      href: '/faq' },
                { label: t('navbar.karir'),    href: '/karir' },
                { label: t('navbar.kandidat'), href: '/kandidat' },
              ].map((link, i) => (
                <Box
                  key={i}
                  component={link.href ? 'a' : 'span'}
                  href={link.href}
                  onClick={link.scroll ? () => handleNav(link.scroll) : undefined}
                  sx={{
                    fontSize: '0.84rem', fontWeight: 500,
                    color: 'rgba(255,255,255,0.5)',
                    textDecoration: 'none',
                    transition: 'color 0.2s, padding-left 0.2s',
                    display: 'block',
                    cursor: 'pointer',
                    '&:hover': { color: '#ff6d00', pl: 0.75 },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* RIGHT — Contact */}
          <Grid item xs={12} md={5}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 2, mb: 3.5 }}>
              {t('footer.header')}
            </Typography>

            <Stack spacing={3.5}>
              <ContactRow
                icon={<WhatsApp sx={{ fontSize: 17, color: '#25D366' }} />}
                label={t('footer.whatsappLabel')}
                value={t('contact.emailAndContact.phone')}
                onClick={openWA}
              />
              <ContactRow
                icon={<Email sx={{ fontSize: 17, color: '#60a5fa' }} />}
                label="Email"
                value={t('contact.emailAndContact.email')}
                href={`mailto:${t('contact.emailAndContact.email')}`}
              />
              <ContactRow
                icon={<LocationOn sx={{ fontSize: 17, color: '#ff6d00' }} />}
                label={t('footer.locationLabel')}
                value={t('contact.location.address')}
                href={t('contact.location.mapLink')}
              />
              <ContactRow
                icon={<AccessTime sx={{ fontSize: 17, color: '#a78bfa' }} />}
                label={t('footer.workingHoursLabel')}
                value={t('footer.workingHours.weekday')}
              />
            </Stack>
          </Grid>

        </Grid>
      </Container>

      {/* ── BOTTOM BAR ── */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2.5 }}
            spacing={1}
          >
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)' }}>
              &copy; {new Date().getFullYear()} {t('footer.companyName')}. All rights reserved.
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.18)', letterSpacing: '0.04em' }}>
              Crafted with care by Rividemeo
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* ── FEEDBACK MODAL ── */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.6)' } } }}
      >
        <Fade in={open}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '92%', maxWidth: 460,
            borderRadius: '24px', overflow: 'hidden',
            outline: 'none', boxShadow: '0 40px 80px rgba(0,0,0,0.45)',
          }}>
            <Box sx={{ position: 'relative', background: 'linear-gradient(135deg, #001533 0%, #002855 100%)', px: 3.5, pt: 3.5, pb: 4, textAlign: 'center', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #2563eb, #ff6d00)' }} />
              <Box sx={{ position: 'absolute', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 300, height: 200, background: 'radial-gradient(ellipse, rgba(255,109,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <IconButton onClick={() => setOpen(false)} size="small" sx={{ position: 'absolute', top: 14, right: 14, zIndex: 1, color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                <Close sx={{ fontSize: 17 }} />
              </IconButton>
              <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Box key={i} onClick={() => setRating(i)} sx={{ cursor: 'pointer', transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.25)' } }}>
                    <FaStar style={{ color: i <= rating ? '#ff6d00' : 'rgba(255,255,255,0.15)', fontSize: '2rem', transition: 'color 0.2s ease', display: 'block' }} />
                  </Box>
                ))}
              </Stack>
              <Typography sx={{ color: '#ff6d00', fontWeight: 800, fontSize: '0.72rem', letterSpacing: 2, textTransform: 'uppercase', mb: 1.75, position: 'relative', zIndex: 1, minHeight: '1em' }}>
                {Array.isArray(ratingLabels) ? ratingLabels[rating] : ''}
              </Typography>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.02em', position: 'relative', zIndex: 1 }}>
                {t('feedback.header')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.8rem', mt: 0.5, position: 'relative', zIndex: 1 }}>
                {t('feedback.subtext')}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: '#fff', px: 3.5, py: 3.5 }}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth label={t('feedback.nameLabel')} placeholder={t('feedback.namePlaceholder')}
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                  sx={FIELD_SX}
                />
                <TextField
                  fullWidth multiline rows={4} label={t('feedback.messageLabel')} placeholder={t('feedback.messagePlaceholder')}
                  value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  sx={FIELD_SX}
                />
                <Button
                  fullWidth onClick={handleSend}
                  startIcon={<WhatsApp sx={{ fontSize: '1.15rem' }} />}
                  sx={{
                    py: 1.65, borderRadius: '12px', bgcolor: '#002855', color: '#fff',
                    fontWeight: 800, fontSize: '0.92rem', textTransform: 'none',
                    boxShadow: '0 6px 20px rgba(0,40,85,0.22)',
                    '&:hover': { bgcolor: '#ff6d00', boxShadow: '0 8px 24px rgba(255,109,0,0.32)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t('feedback.submitButton')}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  )
}

export default Footer
